import { useCallback, useMemo, useState } from "react";
import type { ResourceType } from "frontend/canvas-designer-new/monday-import-items/monday-import-items.types";
import classNames from "classnames";
import { MondayBoardSelector } from "frontend/canvas-designer-new/monday-import-items/monday-board-selector/monday-board-selector";
import { useMondayImportItems } from "frontend/canvas-designer-new/monday-import-items/monday-import.hook";
import { ColumnSelector } from "../../components/column-selector";
import { COLUMN_MAPPING_FIELDS, type OrgChartColumnMapping } from "./synced-org-chart.types";
import { MondayStyledDropdownPicker } from "frontend/ui-components/picker/monday-styled-dropdown-picker/monday-styled-dropdown-picker";
import { OrgChartSkeleton } from "./org-chart-skeleton";
import { OrgChartMappingSkeleton } from "./org-chart-mapping-skeleton";
import { useBoardValue } from "frontend/state/board-state";
import { placeMindMapOrgChart } from "shared/datamodel/mindmap-org-chart";
import { IntegrationType, MondayIntegrationType } from "shared/integrations/integration";
import { SourceNodeType } from "frontend/canvas-designer-new/elements/org-chart/orgchart-utils";
import ConfirmButton from "../../components/confirm-button";
import { useSyncedOrgChart } from "./use-synced-org-chart";
import styles from "./synced-org-chart.module.css";
import Divider from "../../components/divider";
import SyncedWidgetHeader from "../../components/synced-widget-header";
import { useBoardIntegrations } from "frontend/hooks/use-board-integrations";
import { getColumnText, orgChartBoardFilter } from "./utils";

const Arrow = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="11" height="6" viewBox="0 0 11 6" fill="none">
    <path d="M8.3457 0.500035L10.3873 2.5415" stroke="#7F869B" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M8.3457 4.58057L10.3873 2.5391" stroke="#7F869B" strokeLinecap="round" strokeLinejoin="round" />
    <line x1="0.5" y1="2.54932" x2="9.70818" y2="2.54932" stroke="#7F869B" strokeLinecap="round" />
  </svg>
);

interface ItemWithDisplayName {
  id: string;
  name: string;
  displayName: string;
}

export default function SyncedOrgChart({ onComplete }: { onComplete: (data: unknown) => void }) {
  const [{ board }] = useBoardValue();
  const { mondayIntegration, addBoardIntegration } = useBoardIntegrations(board?.documentId);

  const {
    workspaces,
    workspacesLoading,
    workspacesInitialLoading,
    loadMoreWorkspaces,
    selectedWorkspace,
    setSelectedWorkspace,
    boards,
    boardsLoading,
    boardsInitialLoading,
    loadMoreBoards,
    selectedBoard,
    setSelectedBoard,
    setBoardSearch,
    items: groupedItems,
    itemsLoading,
  } = useMondayImportItems({
    withColumns: true,
    withColumnValues: true,
    boardCompatibilityFilter: orgChartBoardFilter,
  });

  const {
    columns,
    columnMapping,
    handleColumnSelect,
    rootItem,
    setRootItem,
    isCreateDisabled,
    getColumnsForField,
    isColumnEnabled,
  } = useSyncedOrgChart(selectedBoard);

  const items: ItemWithDisplayName[] = useMemo(() => {
    const rawItems = groupedItems.flatMap((group) => group.items);
    return rawItems.map((item) => {
      const displayName = getColumnText(item.column_values, columnMapping.employeeName) || item.name;
      return {
        id: item.id,
        name: item.name,
        displayName,
      };
    });
  }, [columnMapping.employeeName, groupedItems]);

  const handleBoardSelect = useCallback(
    (pickedBoard: ResourceType) => {
      const fullBoard = boards.find((b) => b.id === pickedBoard.id);
      setSelectedBoard(fullBoard ?? null);
    },
    [boards, setSelectedBoard]
  );

  const [isCreating, setIsCreating] = useState(false);

  const handleCreateOrgChart = useCallback(async () => {
    if (
      isCreateDisabled ||
      !mondayIntegration ||
      !rootItem ||
      !selectedBoard ||
      !columnMapping.employeeName ||
      !columnMapping.directManager ||
      !columnMapping.position
    ) {
      return;
    }

    setIsCreating(true);

    try {
      // Create the board integration
      const configuration = {
        boardId: selectedBoard.id,
        columns,
        type: MondayIntegrationType.org_chart,
        columnMapping: {
          selfId: columnMapping.employeeName,
          parentId: columnMapping.directManager,
          jobTitle: columnMapping.position,
        },
        itemId: rootItem.id,
      };

      const boardIntegrationId = await addBoardIntegration(mondayIntegration.id, configuration);

      // Create the org chart - items array just needs the root item ID
      const orgChartData = placeMindMapOrgChart(
        boardIntegrationId,
        IntegrationType.monday,
        configuration,
        { x: 0, y: 0 },
        items,
        SourceNodeType.Person,
        false,
        rootItem.id
      );

      onComplete(orgChartData);
    } catch (error) {
      console.error("Error creating org chart:", error);
    } finally {
      setIsCreating(false);
    }
  }, [
    isCreateDisabled,
    mondayIntegration,
    addBoardIntegration,
    rootItem,
    selectedBoard,
    columns,
    columnMapping,
    items,
    onComplete,
  ]);

  if (workspacesInitialLoading) {
    return <OrgChartSkeleton />;
  }

  const showMappingSkeleton = selectedBoard && boardsLoading && columns.length === 0;

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.selectorsRow}>
          <MondayBoardSelector
            workspaces={workspaces}
            selectedWorkspace={selectedWorkspace}
            setSelectedWorkspace={setSelectedWorkspace}
            workspacesLoading={workspacesLoading}
            workspacesInitialLoading={workspacesInitialLoading}
            loadMoreWorkspaces={loadMoreWorkspaces}
            boards={boards}
            selectedBoard={selectedBoard}
            setSelectedBoard={handleBoardSelect}
            boardsLoading={boardsLoading}
            boardsInitialLoading={boardsInitialLoading}
            loadMoreBoards={loadMoreBoards}
            workspacePickerTitle="Select workspace"
            boardPickerTitle="Select board"
            isBoardDisabled={(board) => !!board && !(board as { isCompatible?: boolean }).isCompatible}
            disabledBoardTooltip="This board is missing required columns"
            pickerWidth="158px"
            pickerStyle={{
              color: "#1E1E1E",
              fontSize: "10px",
              fontWeight: 400,
              marginBottom: "4px",
            }}
            onSearch={setBoardSearch}
          />
        </div>

        <SyncedWidgetHeader title="Map out your Org chart" subtitle="Select the relevant columns from the board" />
      </div>

      <Divider />

      {showMappingSkeleton ? (
        <OrgChartMappingSkeleton />
      ) : (
        <div className={styles.mappingSection}>
          {Object.entries(COLUMN_MAPPING_FIELDS).map(([key, config]) => {
            const fieldKey = key as keyof OrgChartColumnMapping;
            const sortedColumns = getColumnsForField(config.validTypes, columnMapping[fieldKey]);
            return (
              <div key={key} className={styles.mappingRow}>
                <div
                  className={classNames(styles.mappingLabel, {
                    [styles.mappingLabelActive]: columns.length > 0,
                  })}
                >
                  {config.label} {config.labelWithArrow && <Arrow />}
                </div>
                <ColumnSelector
                  columns={sortedColumns}
                  selectedColumn={columnMapping[fieldKey]}
                  onSelect={(columnId) => handleColumnSelect(fieldKey, columnId)}
                  placeholder={config.placeholder}
                  disabled={!selectedBoard || columns.length === 0}
                  isColumnDisabled={(column) => !!column && !isColumnEnabled(column.id, config.validTypes)}
                  disabledColumnTooltip="Supports Text or Person columns"
                />
              </div>
            );
          })}

          <div className={styles.mappingRow}>
            <div
              className={classNames(styles.mappingLabel, {
                [styles.mappingLabelActive]: items.length > 0,
              })}
            >
              Name for top of chart:
            </div>
            <MondayStyledDropdownPicker<ItemWithDisplayName>
              options={items}
              selectedOption={rootItem ? items.find((item) => item.id === rootItem.id) ?? null : null}
              onChange={setRootItem}
              isSelected={(item) => item.id === rootItem?.id}
              getTitle={(item) => item?.displayName || ""}
              renderOption={(item) => <div className={styles.itemOption}>{item?.displayName || "Unknown"}</div>}
              placeholder="Choose name"
              disabled={!selectedBoard || items.length === 0 || !columnMapping.employeeName}
              isLoading={itemsLoading}
              width="158px"
              enableSearch={true}
              searchPlaceholder="Search..."
              useFloatingDropdown={true}
              trackingEventName="cim_org_chart_root_item_selected"
            />
          </div>
          <div className={styles.createButtonContainer}>
            <ConfirmButton
              title={isCreating ? "Creating..." : "Create"}
              disabled={isCreateDisabled || !mondayIntegration}
              onClick={handleCreateOrgChart}
              isLoading={isCreating}
              trackingEventName="cim_org_chart_created"
            />
          </div>
        </div>
      )}
    </div>
  );
}
